import React from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import {
  AlertCircle,
  Bell,
  Search,
  Send,
} from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { hasAnyPolicy } from "@/utils/policies";
import { useUser } from "../../../store/useUser";
import { TabHeader } from "../shared/components/TabHeader";
import { TabToolbar } from "../shared/components/TabToolbar";

const GET_NOTIFICATION_PROGRAMS = gql`
  query GetNotificationProgramsForParticipantTab {
    notificationLogPrograms {
      id
      name
      slug
    }
  }
`;

const GET_NOTIFICATION_SEND_LOG = gql`
  query ParticipantNotificationSendLog(
    $participantIDs: [Int]
    $programID: Int
    $search: String
    $limit: Int
    $offset: Int
  ) {
    notificationSendLog(
      participantIDs: $participantIDs
      programID: $programID
      search: $search
      limit: $limit
      offset: $offset
    ) {
      id
      source
      programID
      programSlug
      programName
      templateName
      subject
      body
      sentAt
    }
  }
`;

const DUMMY_SEND_NOTIFICATION = gql`
  mutation DummySendNotification(
    $programID: Int
    $participantID: Int!
    $toAddress: String!
    $ccAddress: String
    $replyToAddress: String
    $subject: String!
    $body: String!
  ) {
    dummySendNotification(
      programID: $programID
      participantID: $participantID
      toAddress: $toAddress
      ccAddress: $ccAddress
      replyToAddress: $replyToAddress
      subject: $subject
      body: $body
    ) {
      id
      subject
      participantID
      sentAt
    }
  }
`;

interface NotificationProgram {
  id: number;
  name?: string | null;
  slug: string;
}

interface NotificationProgramsData {
  notificationLogPrograms: NotificationProgram[];
}

interface NotificationSendLogEntry {
  id: number;
  source?: string | null;
  programID?: number | null;
  programSlug?: string | null;
  programName?: string | null;
  templateName?: string | null;
  subject?: string | null;
  body?: string | null;
  sentAt?: string | null;
}

interface NotificationSendLogData {
  notificationSendLog: NotificationSendLogEntry[];
}

interface DummySendNotificationData {
  dummySendNotification: NotificationSendLogEntry;
}

interface SendFormState {
  toAddress: string;
  ccAddress: string;
  replyToAddress: string;
  subject: string;
  body: string;
}

interface ProgramDetail {
  programDescription?: string | null;
  programParticipantNumber?: string | number | null;
}

interface NotificationsProps {
  programName: string;
  odrNumber?: number | null;
  programParticipantNumber?: string | number | null;
  programDetails?: ProgramDetail[];
}

const PROGRAMS = [
  { slug: "mist", aliases: ["mist"], sendmailPath: "/sendmail.php", programSlugs: ["mist"] },
  { slug: "fist", aliases: ["fist"], sendmailPath: "/fist/sendmail.php", programSlugs: ["fist"] },
  { slug: "odrh", aliases: ["odr", "odrh", "zodr", "housing"], sendmailPath: "/odrhousing/sendmail.php", programSlugs: ["odrh", "odr"] },
  { slug: "prg_wmn", aliases: ["prg_wmn", "prg wmn", "maternal", "pregnant"], sendmailPath: "/prg_wmn/sendmail.php", programSlugs: ["prg_wmn"] },
  { slug: "lead", aliases: ["lead"], sendmailPath: "/lead/sendmail.php", programSlugs: ["lead"] },
  { slug: "dsh", aliases: ["dsh"], sendmailPath: "/dsh/sendmail.php", programSlugs: ["dsh"] },
] as const;

const resolveProgram = (value?: string | null) => {
  const normalized = (value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  return (
    PROGRAMS.find((program) =>
      program.aliases.some((alias) => normalized === alias || normalized.includes(alias))
    ) || null
  );
};

const toPositiveInt = (value?: string | number | null) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const formatNotificationDate = (value?: string | null) => {
  if (!value) {
    return "N/A";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDifference = Math.round((startOfToday - startOfDate) / 86400000);

  if (dayDifference === 0) {
    return "Today";
  }
  if (dayDifference === 1) {
    return "Yesterday";
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
};

const readableText = (value?: string | null) => {
  return (value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/\r/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n\s+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const compactText = (value?: string | null, maxLength = 140) => {
  const text = readableText(value).replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) {
    return text || "N/A";
  }
  return `${text.slice(0, maxLength - 3)}...`;
};

const sourceLabel = (value?: string | null) => {
  if (value === "nova_dummy_send") {
    return "Nova Send";
  }
  if (value === "notification_template") {
    return "Template";
  }
  if (value === "secure_mail") {
    return "Secure Mail";
  }
  if (value === "public_referral") {
    return "Public Referral";
  }
  return value || "System";
};

const notificationTitle = (entry: NotificationSendLogEntry) => {
  return readableText(entry.subject) || entry.templateName || sourceLabel(entry.source);
};

const notificationType = (entry: NotificationSendLogEntry) => {
  return entry.templateName || sourceLabel(entry.source);
};

const ErrorState = ({ error }: { error: Error }) => (
  <Alert variant="destructive">
    <AlertCircle className="h-4 w-4" />
    <AlertDescription>
      Error loading notifications: {error.message}
    </AlertDescription>
  </Alert>
);

const emptySendForm = (): SendFormState => ({
  toAddress: "",
  ccAddress: "",
  replyToAddress: "",
  subject: "",
  body: "",
});

export const Notifications: React.FC<NotificationsProps> = ({
  programName,
  odrNumber,
  programParticipantNumber,
  programDetails = [],
}) => {
  const { user } = useUser();
  const canReadNotifications = hasAnyPolicy(user, ["program:participant:notifications~full"]);
  const canManageNotifications = hasAnyPolicy(user, ["tools:notifications~full"]);
  const [historySearch, setHistorySearch] = React.useState("");
  const [historyProgramID, setHistoryProgramID] = React.useState<number | null>(null);
  const [selectedHistoryID, setSelectedHistoryID] = React.useState<number | null>(null);
  const [sendOpen, setSendOpen] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [sendForm, setSendForm] = React.useState<SendFormState>(() => emptySendForm());

  const program = resolveProgram(programName);
  const selectedParticipantNumber = toPositiveInt(programParticipantNumber);
  const participantIDs = React.useMemo(() => {
    const ids = new Set<number>();
    const odrID = toPositiveInt(odrNumber);

    if (odrID) {
      ids.add(odrID);
    }
    if (selectedParticipantNumber) {
      ids.add(selectedParticipantNumber);
    }
    for (const detail of programDetails) {
      const id = toPositiveInt(detail?.programParticipantNumber);
      if (id) {
        ids.add(id);
      }
    }

    return Array.from(ids);
  }, [odrNumber, programDetails, selectedParticipantNumber]);

  const { data: programsData, loading: programsLoading, error: programsError } = useQuery<NotificationProgramsData>(
    GET_NOTIFICATION_PROGRAMS,
    { skip: !canReadNotifications }
  );

  const programs = programsData?.notificationLogPrograms || [];
  const programId = React.useMemo(() => {
    if (!program) {
      return null;
    }
    const programSlugs: readonly string[] = program.programSlugs;
    const match = programs.find((entry) => programSlugs.includes(entry.slug));
    return match?.id || null;
  }, [program, programs]);

  const historyVariables = React.useMemo(
    () => ({
      participantIDs,
      programID: historyProgramID || undefined,
      search: historySearch.trim() || undefined,
      limit: 200,
      offset: 0,
    }),
    [historyProgramID, historySearch, participantIDs]
  );

  const {
    data: historyData,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery<NotificationSendLogData>(GET_NOTIFICATION_SEND_LOG, {
    variables: historyVariables,
    skip: !canReadNotifications || participantIDs.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const [dummySendNotification, { loading: sending }] = useMutation<DummySendNotificationData>(DUMMY_SEND_NOTIFICATION);
  const historyEntries = historyData?.notificationSendLog || [];
  const selectedHistoryEntry = React.useMemo(
    () => historyEntries.find((entry) => entry.id === selectedHistoryID) || historyEntries[0] || null,
    [historyEntries, selectedHistoryID]
  );

  React.useEffect(() => {
    if (historyEntries.length === 0) {
      setSelectedHistoryID(null);
      return;
    }
    if (!selectedHistoryID || !historyEntries.some((entry) => entry.id === selectedHistoryID)) {
      setSelectedHistoryID(historyEntries[0].id);
    }
  }, [historyEntries, selectedHistoryID]);

  if (!canReadNotifications) {
    return (
      <div className="space-y-3">
        <TabHeader
          icon={Bell}
          title="Notifications"
          subtitle="Access required"
        />
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You do not have permission to view notifications.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const sendParticipantID = selectedParticipantNumber || participantIDs[0] || null;

  const openSendDialog = () => {
    setSendError(null);
    setSendForm(emptySendForm());
    setSendOpen(true);
  };

  const updateSendForm = (field: keyof SendFormState, value: string) => {
    setSendForm((current) => ({ ...current, [field]: value }));
  };

  const submitDummySend = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSendError(null);

    if (!sendParticipantID) {
      setSendError("No participant identifier is available.");
      return;
    }

    const toAddress = sendForm.toAddress.trim();
    const subject = sendForm.subject.trim();
    const body = sendForm.body.trim();

    if (!toAddress || !subject || !body) {
      setSendError("To, subject, and message are required.");
      return;
    }

    try {
      await dummySendNotification({
        variables: {
          programID: programId || undefined,
          participantID: sendParticipantID,
          toAddress,
          ccAddress: sendForm.ccAddress.trim() || undefined,
          replyToAddress: sendForm.replyToAddress.trim() || undefined,
          subject,
          body,
        },
      });

      setHistorySearch("");
      setHistoryProgramID(null);
      await refetchHistory({
        participantIDs,
        programID: undefined,
        search: undefined,
        limit: 200,
        offset: 0,
      });
      setSendOpen(false);
      setSendForm(emptySendForm());
    } catch (error: any) {
      setSendError(error?.message || "Unable to send notification.");
    }
  };

  return (
    <div className="space-y-4">
      <TabHeader
        icon={Bell}
        title="Notifications"
        subtitle={`${historyEntries.length} notification${historyEntries.length === 1 ? "" : "s"} found`}
        action={
          canManageNotifications ? (
            <Button
              size="sm"
              className="h-8 gap-2"
              onClick={openSendDialog}
              disabled={participantIDs.length === 0}
            >
              <Send className="h-4 w-4" />
              Send
            </Button>
          ) : null
        }
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent contentWidth="720px" contentHeight="auto" contentMaxWidth="calc(100vw - 32px)">
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submitDummySend}>
            <div className="space-y-2">
              <Label htmlFor="notification-participant">Participant</Label>
              <Input
                id="notification-participant"
                value={sendParticipantID ? String(sendParticipantID) : ""}
                readOnly
                className="h-9 bg-gray-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-to">To</Label>
              <Input
                id="notification-to"
                value={sendForm.toAddress}
                onChange={(event) => updateSendForm("toAddress", event.target.value)}
                placeholder="name@example.org"
                className="h-9"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="notification-cc">CC</Label>
                <Input
                  id="notification-cc"
                  value={sendForm.ccAddress}
                  onChange={(event) => updateSendForm("ccAddress", event.target.value)}
                  placeholder="Optional"
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification-reply-to">Reply To</Label>
                <Input
                  id="notification-reply-to"
                  value={sendForm.replyToAddress}
                  onChange={(event) => updateSendForm("replyToAddress", event.target.value)}
                  placeholder="Optional"
                  className="h-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-subject">Subject</Label>
              <Input
                id="notification-subject"
                value={sendForm.subject}
                onChange={(event) => updateSendForm("subject", event.target.value)}
                className="h-9"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-body">Message</Label>
              <Textarea
                id="notification-body"
                value={sendForm.body}
                onChange={(event) => updateSendForm("body", event.target.value)}
                className="min-h-[140px]"
              />
            </div>

            {sendError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{sendError}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="flex-row justify-end">
              <Button type="submit" disabled={sending}>
                {sending ? "Sending..." : "Send Notification"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {programsError ? <ErrorState error={programsError} /> : null}
      {historyError ? <ErrorState error={historyError} /> : null}

      <div className="space-y-3">
          <TabToolbar
            left={
              <>
                <div className="relative w-full sm:w-80">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={historySearch}
                    onChange={(event) => setHistorySearch(event.target.value)}
                    placeholder="Search notifications"
                    className="h-9 pl-8"
                  />
                </div>
                <Select
                  value={historyProgramID ? String(historyProgramID) : "all"}
                  onValueChange={(value) => setHistoryProgramID(value === "all" ? null : Number(value))}
                >
                  <SelectTrigger className="h-9 w-[190px]">
                    <SelectValue placeholder="All programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    {programs.map((entry) => (
                      <SelectItem key={entry.id} value={String(entry.id)}>
                        {entry.name || entry.slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
          />

          {participantIDs.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>No participant identifiers are available for this profile.</AlertDescription>
            </Alert>
          ) : null}

          <Card className="rounded-none border-0 shadow-none">
            <CardContent className="p-0">
              <div className="border-y border-gray-200">
                <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-500">
                  {historyEntries.length} notification{historyEntries.length === 1 ? "" : "s"}
                </div>
                <div className="divide-y divide-gray-100">
                  {historyLoading && historyEntries.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      Loading notifications...
                    </div>
                  ) : null}
                  {!historyLoading && historyEntries.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      No notifications found.
                    </div>
                  ) : null}
                  {historyEntries.map((entry) => {
                    const expanded = selectedHistoryEntry?.id === entry.id;
                    return (
                      <article key={entry.id} className={expanded ? "bg-blue-50/40" : "bg-white"}>
                        <button
                          type="button"
                          onClick={() => setSelectedHistoryID(entry.id)}
                          aria-expanded={expanded}
                          className="block w-full px-4 py-3 text-left hover:bg-gray-50"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-gray-900">
                                {notificationTitle(entry)}
                              </div>
                              {!expanded ? (
                                <div className="mt-1 text-xs leading-5 text-gray-500">
                                  {compactText(entry.body, 180)}
                                </div>
                              ) : null}
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className="border-blue-100 bg-blue-50 text-blue-700">
                                  {notificationType(entry)}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {entry.programName || entry.programSlug || "Program N/A"}
                                </span>
                              </div>
                            </div>
                            <div className="shrink-0 whitespace-nowrap text-xs text-gray-500">
                              {formatNotificationDate(entry.sentAt)}
                            </div>
                          </div>
                        </button>
                        {expanded ? (
                          <div className="px-4 pb-4 pt-1">
                            <div className="whitespace-pre-wrap break-words border-l-2 border-blue-200 pl-4 text-sm leading-6 text-gray-800">
                              {readableText(entry.body) || "No message body was recorded for this notification."}
                            </div>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
      </div>
    </div>
  );
};

export default Notifications;
